import { useAdminListState } from '../context/AdminListStateContext'
import { useTableSelection } from './useTableSelection'
import { usePersonalTableViews } from './usePersonalTableViews'
import { normalizeViewConfiguration, viewQuery } from '../services/tableViews'
import type { TableSelectionConfig } from '../services/tableState'
import { useCallback, useMemo } from 'react'
import { usePaginatedList } from '../components/workspaces/admin/usePaginatedList'
import { useWorkspaceTableColumns } from './useTableColumns'
import { useWorkspaceDensity } from './useInterfaceDensity'
import { normalizeTableView, type TableDefinition, type TableFilterValue } from '../services/tableState'
import type { ListViewState } from '../context/AdminListStateContext'
import type { ListQuery, PaginatedResponse } from '../types/access'

/** Compose the existing pager, verified preference adapter and density hook. Never fetch/sort records here. */
export function useSmartTable<T, F extends Record<string, TableFilterValue>>(definition: TableDefinition<F>, options: {
  enabled?: boolean
  source?: unknown
  selection?: TableSelectionConfig<T>
  refineItems?: (items: T[], view: ListViewState) => T[]
  lockedFilters?: Partial<F>
  fetchPage: (query: ListQuery & F, signal?: AbortSignal) => Promise<PaginatedResponse<T>>
}) {
  const lockedKey = JSON.stringify(options.lockedFilters || {})
  const normalizeView = useCallback((view: ListViewState) => normalizeTableView(view, definition, options.lockedFilters), [definition, lockedKey])
  const defaults = useMemo(() => Object.fromEntries(definition.filters.map(filter => [filter.id, filter.defaultValue])) as F, [definition])
  const list = usePaginatedList<T,F>({ ...options, stateKey: definition.stateKey, defaultSortBy: definition.defaultSort.id,
    queryFilterKeys: definition.filters.filter(filter => filter.scope === 'source').map(filter => filter.id),
    defaultSortDir: definition.defaultSort.direction, defaultFilters: defaults, normalizeView,
    fetchPage: (query, signal) => {
      const sourceQuery: Record<string, unknown> = { page: query.page, pageSize: query.pageSize, search: query.search, sortBy: query.sortBy, sortDir: query.sortDir }
      for (const filter of definition.filters) if (filter.scope === 'source') sourceQuery[filter.id] = query[filter.id]
      return options.fetchPage(sourceQuery as ListQuery & F, signal)
    },
  })
  const preference = useWorkspaceTableColumns(definition.workspaceId, definition.id, definition.columns)
  const density = useWorkspaceDensity(definition.densityKey)
  const activeFilters = definition.filters.filter(filter => list.filters[filter.id] !== undefined && list.filters[filter.id] !== '' && list.filters[filter.id] !== filter.defaultValue
    && !Object.prototype.hasOwnProperty.call(options.lockedFilters || {}, filter.id))
  const context = useAdminListState()
  const verified = Boolean(preference.scope && context?.scopeKey && (options.enabled ?? true))
  const visibleItems = useMemo(() => options.refineItems ? options.refineItems(list.items,list.view) : list.items,
    [list.items,options.refineItems,list.view.search,JSON.stringify(list.filters)])
  const datasetKey = JSON.stringify(list.view)
  const selectionScope = useMemo(() => verified ? {} : null, [verified,context,preference.contextRevision,definition.workspaceId,definition.id])
  const selection = useTableSelection(visibleItems,options.selection,selectionScope,datasetKey,verified && !list.loading && !list.error)
  const configuration = normalizeViewConfiguration({columns:{hiddenColumnIds:preference.hiddenColumnIds},
    sort:{columnId:list.sortBy,direction:list.sortDir}, filters:list.filters,density:density.density,pagination:{pageSize:list.view.pageSize}},definition)
  const transientCriteria = Boolean(list.search.trim()) || definition.filters.some(filter=>!filter.persist
    && !Object.prototype.hasOwnProperty.call(options.lockedFilters||{},filter.id) && list.filters[filter.id] !== undefined && list.filters[filter.id] !== '' && list.filters[filter.id] !== filter.defaultValue)
  const views = usePersonalTableViews({definition,scope:preference.scope,revision:preference.contextRevision,enabled:verified,configuration,transientCriteria,
    apply: configuration => {
      selection.clear()
      list.applyView(viewQuery(configuration,definition,options.lockedFilters||{}))
      preference.setHiddenColumnIds(configuration.columns.hiddenColumnIds)
      density.setDensity(configuration.density)
    }})
  return { ...list, definition, preference, density, activeFilters, selection, views, visibleItems,
    activeFilterCount: activeFilters.length + (list.search.trim() ? 1 : 0),
    setFilter: <K extends Extract<keyof F, string>>(key: K, value: F[K]) => {
      const filter = definition.filters.find(filter => filter.id === key)
      if (filter) list.setFilter(key, value, filter.scope === 'source')
    },
    setSort: (key: string) => { if (definition.sorts.some(sort => sort.id === key)) list.setSort(key) },
    setSorting: (key: string, direction: 'asc' | 'desc') => { if (definition.sorts.some(sort => sort.id === key)) list.setSorting(key, direction) },
    clearSort: () => list.setSorting(definition.defaultSort.id, definition.defaultSort.direction),
  }
}
